import { useCallback, useEffect, useState } from "react";

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MenuBook as JournalIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  MAX_MANUAL_EDIT_LINES,
  UpdateCostFromFileDialog,
} from "@/features/stock-adjustments";
import {
  GeneralInformationSection,
  StockAdjustmentProductVariantsSection,
} from "@/features/stock-adjustments/stock-adjustment";
import {
  CenteredLoading,
  CustomDialogTitle,
  JournalDetailsDialog,
  PageContainer,
  ResponsiveToolbarButton,
  useSnackbar,
} from "@/features/ui";

export const StockAdjustment = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [stockAdjustment, setStockAdjustment] = useState(null);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [costDialogOpen, setCostDialogOpen] = useState(false);

  const loadStockAdjustment = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`stock-adjustments/${referenceNumber}`, {
        params: {
          withOutlet: true,
          withWarehouse: true,
          withAdjustmentChartOfAccount: true,
        },
      });
      setStockAdjustment(data);
    } catch (error) {
      setError(getErrorMessage(error, "Gagal memuat penyesuaian stok"));
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadStockAdjustment();
  }, [loadStockAdjustment]);

  const handleDelete = async () => {
    if (!stockAdjustment || !outlet) return;

    try {
      setIsDeleting(true);
      await apiOutletClient.delete(`stock-adjustments/${referenceNumber}`, {
        headers: { "Outlet-Id": outlet.id },
      });
      showSnackbar("Penyesuaian stok berhasil dihapus", "success");
      navigate("/inventory/stock-adjustments");
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal menghapus penyesuaian stok"));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const lines = stockAdjustment?.stockAdjustmentProductVariants ?? [];
  const canUpdate = hasPermission(PermissionEnum.UPDATE_STOCK_ADJUSTMENTS);
  // Past this size the manual edit form is too heavy; costs are changed from a file instead.
  const tooLargeToEdit = lines.length > MAX_MANUAL_EDIT_LINES;
  const hasStockInLine = lines.some((line) => line.quantity > 0);

  const toolbarContent = (
    <>
      {hasPermission(PermissionEnum.DELETE_STOCK_ADJUSTMENTS) && (
        <ResponsiveToolbarButton
          label="Hapus"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        />
      )}
      {canUpdate && hasStockInLine && (
        <ResponsiveToolbarButton
          label="Update Harga dari File"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setCostDialogOpen(true)}
        />
      )}
      {canUpdate && !tooLargeToEdit && (
        <ResponsiveToolbarButton
          label="Ubah"
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate("edit")}
        />
      )}
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
    </>
  );

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Penyesuaian Stok"
      breadcrumbs={[
        {
          title: "Penyesuaian Stok",
          path: "/inventory/stock-adjustments",
        },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        {canUpdate && tooLargeToEdit && (
          <Alert severity="info">
            Penyesuaian stok ini berisi {lines.length.toLocaleString("id-ID")}{" "}
            baris, terlalu banyak untuk diubah manual (maksimal{" "}
            {MAX_MANUAL_EDIT_LINES} baris). Gunakan &quot;Update Harga dari
            File&quot; untuk mengubah harga per unit.
          </Alert>
        )}
        <GeneralInformationSection stockAdjustment={stockAdjustment} />
        <StockAdjustmentProductVariantsSection
          stockAdjustmentProductVariants={
            stockAdjustment.stockAdjustmentProductVariants || []
          }
          warehouse={stockAdjustment.warehouse}
        />

        <UpdateCostFromFileDialog
          open={costDialogOpen}
          onClose={() => setCostDialogOpen(false)}
          stockAdjustment={stockAdjustment}
          onUpdated={() => {
            setCostDialogOpen(false);
            loadStockAdjustment();
          }}
        />

        {/* Journal Details Dialog */}
        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="STOCK_ADJUSTMENT"
          transactionableId={stockAdjustment?.id}
        />

        {deleteDialogOpen && (
          <Dialog
            open
            onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <CustomDialogTitle
              title="Hapus Penyesuaian Stok"
              onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            />
            <DialogContent>
              <DialogContentText>
                Apakah Anda yakin ingin menghapus penyesuaian stok{" "}
                <strong>{referenceNumber}</strong>?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                Batal
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                disabled={isDeleting}
              >
                {isDeleting ? "Menghapus..." : "Hapus"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Stack>
    </PageContainer>
  );
};
